import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import get from 'lodash/get';
import { Eye, EyeOff } from 'lucide-react';

import {
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  OutlinedInputProps,
} from '@mui/material';

import { IconButton } from '../button';

interface PasswordFieldProps extends OutlinedInputProps {
  helperText?: string;
  name: string;
}

export const PasswordField: React.FC<PasswordFieldProps> = ({
  helperText = '',
  name,
  ...props
}) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    control,
    formState: { defaultValues, errors, isSubmitting },
  } = useFormContext();

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const errorMessage = get(errors, name)?.message as string | undefined;

  return (
    <Controller
      render={({ field }) => (
        <FormControl
          disabled={isSubmitting || props.disabled}
          error={!!errorMessage || props.error}
          required={props.required}
        >
          <InputLabel htmlFor={props.id}>{props.label}</InputLabel>
          <OutlinedInput
            size="medium"
            type={showPassword ? 'text' : 'password'}
            {...props}
            {...field}
            endAdornment={
              <InputAdornment position="end" sx={{ pr: 1 }}>
                <IconButton
                  aria-label="toggle password visibility"
                  edge="end"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </IconButton>
              </InputAdornment>
            }
            disabled={isSubmitting || props.disabled}
          />
          <FormHelperText>{errorMessage || helperText}</FormHelperText>
        </FormControl>
      )}
      control={control}
      defaultValue={defaultValues?.[name] ?? ''}
      name={name}
    />
  );
};
